import { CSS_SUBSET_VERSION, STYLE_FEATURE_BITS, STYLE_INTERACTION_STATE_MASK, STYLE_STATE_PROPERTIES, STYLE_PROPERTIES, } from "./generated";
import { isInternalStyleSheet } from "./internal";
import { expandDeclaration, metadataForProperty } from "./values";
const classNamePattern = /^[_a-zA-Z][_a-zA-Z0-9-]*$/u;
const properties = Object.values(STYLE_PROPERTIES);
const featureBits = Object.values(STYLE_FEATURE_BITS).reduce((bits, value) => bits | value, 0);
const capabilitySnapshot = Object.freeze({
    cssSubsetVersion: CSS_SUBSET_VERSION,
    featureBits,
    resolverReady: true,
    engineReady: true,
    interactionStateMask: STYLE_INTERACTION_STATE_MASK,
    stateProperties: Object.freeze([...STYLE_STATE_PROPERTIES]),
    properties: Object.freeze(properties.map((property) => Object.freeze({
        ...property,
        invalidation: Object.freeze([...property.invalidation]),
        appliesTo: Object.freeze([...property.appliesTo]),
        affects: Object.freeze([...property.affects]),
    }))),
});
/** Resolves initial/inherited values, registered class rules, inline style, and legacy props. */
export function resolveStyle(options) {
    const diagnostics = [];
    const classes = parseClassName(options.className, diagnostics);
    const candidates = new Map();
    let order = 0;
    const interactionState = normalizeInteractionState(options.interactionState);
    for (const styleSheet of options.styleSheets ?? []) {
        if (!isInternalStyleSheet(styleSheet) || styleSheet.cssSubsetVersion !== CSS_SUBSET_VERSION) {
            diagnostics.push({
                code: "invalid-css",
                severity: "error",
                message: `Stylesheet is not compatible with CSS subset ${CSS_SUBSET_VERSION}`,
            });
            continue;
        }
        for (const rule of styleSheet.rules) {
            if (!rule.classes.every((className) => classes.has(className)))
                continue;
            if ((interactionState & rule.stateMask) !== rule.stateMask)
                continue;
            for (const declaration of rule.declarations) {
                order += 1;
                considerCandidate(declaration.property, declaration.value, rule.specificity, order, options, candidates, diagnostics);
            }
        }
    }
    order = addObjectDeclarations(options.inlineStyle, 1_000_000, order, options, candidates, diagnostics, false);
    addObjectDeclarations(options.legacyStyle, 2_000_000, order, options, candidates, diagnostics, true);
    const computed = {};
    for (const metadata of properties) {
        if (!metadata.appliesTo.includes(options.nodeType))
            continue;
        const inherited = metadata.inherited ? options.parentStyle?.[metadata.jsName] : undefined;
        const candidate = candidates.get(metadata.jsName);
        computed[metadata.jsName] = resolveSpecifiedValue(candidate?.value, metadata.initial, inherited, metadata.inherited);
    }
    applyOverflowCoupling(computed);
    resolveCurrentColor(computed);
    return Object.freeze({
        style: Object.freeze(computed),
        diagnostics: Object.freeze(diagnostics),
    });
}
/** Resolves every M6 interaction-state combination and retains only changed state properties. */
export function resolveInteractionStyles(options) {
    const base = resolveStyle(options);
    const variants = [];
    for (let stateMask = 1; stateMask <= STYLE_INTERACTION_STATE_MASK; stateMask += 1) {
        const resolved = resolveStyle({ ...options, interactionState: stateMask });
        const changed = {};
        for (const property of STYLE_STATE_PROPERTIES) {
            const value = resolved.style[property];
            if (value !== undefined && !Object.is(value, base.style[property]))
                changed[property] = value;
        }
        if (Object.keys(changed).length > 0) {
            variants.push(Object.freeze({ stateMask, style: Object.freeze(changed) }));
        }
    }
    return Object.freeze({
        style: base.style,
        diagnostics: base.diagnostics,
        variants: Object.freeze(variants),
    });
}
function normalizeInteractionState(value) {
    if (value === undefined)
        return 0;
    if (!Number.isInteger(value) || value < 0 || (value & ~STYLE_INTERACTION_STATE_MASK) !== 0) {
        throw new RangeError("interactionState contains unsupported state bits");
    }
    return value;
}
/** Returns whether the M6-A Shell resolver accepts a property/value pair. */
export function supportsStyle(property, value) {
    const syntax = property.includes("-") ? "css" : "js";
    const result = expandDeclaration(property, value, syntax);
    return result.diagnostics.length === 0 && result.declarations.length > 0;
}
/** Returns the immutable delivered M6 Shell/Core capability snapshot. */
export function styleCapabilities() {
    return capabilitySnapshot;
}
function addObjectDeclarations(style, specificity, startOrder, options, candidates, diagnostics, legacy) {
    let order = startOrder;
    if (style === undefined)
        return order;
    for (const [name, value] of Object.entries(style)) {
        const expanded = expandDeclaration(name, value, "js");
        diagnostics.push(...expanded.diagnostics);
        for (const declaration of expanded.declarations) {
            order += 1;
            if (legacy && candidates.has(declaration.property)) {
                const metadata = metadataForProperty(declaration.property);
                diagnostics.push({
                    code: "legacy-direct-prop-conflict",
                    severity: "warning",
                    message: `Legacy direct prop ${metadata.jsName} overrides a CSS declaration`,
                    property: metadata.cssName,
                });
            }
            considerCandidate(declaration.property, declaration.value, specificity, order, options, candidates, diagnostics);
        }
    }
    return order;
}
function considerCandidate(property, value, specificity, order, options, candidates, diagnostics) {
    const metadata = metadataForProperty(property);
    if (!metadata.appliesTo.includes(options.nodeType)) {
        diagnostics.push({
            code: "property-not-applicable",
            severity: "error",
            message: `${metadata.cssName} does not apply to ${options.nodeType}`,
            property: metadata.cssName,
        });
        return;
    }
    const current = candidates.get(property);
    if (current === undefined ||
        specificity > current.specificity ||
        (specificity === current.specificity && order > current.order)) {
        candidates.set(property, { specificity, order, value });
    }
}
function resolveSpecifiedValue(value, initial, inherited, propertyInherits) {
    if (value === undefined)
        return inherited ?? initial;
    if (typeof value !== "object")
        return value;
    switch (value.global) {
        case "inherit":
            return inherited ?? initial;
        case "initial":
            return initial;
        case "unset":
            return propertyInherits ? (inherited ?? initial) : initial;
    }
}
function parseClassName(value, diagnostics) {
    const result = new Set();
    if (value === undefined || value.trim() === "")
        return result;
    for (const className of value.trim().split(/\s+/u)) {
        if (!classNamePattern.test(className)) {
            diagnostics.push({
                code: "invalid-class-name",
                severity: "error",
                message: `Invalid class token ${JSON.stringify(className)}`,
            });
        }
        else {
            result.add(className);
        }
    }
    return result;
}
function applyOverflowCoupling(computed) {
    const x = computed.overflowX;
    const y = computed.overflowY;
    if (typeof x !== "string" || typeof y !== "string")
        return;
    const xNonScrollable = x === "visible" || x === "clip";
    const yNonScrollable = y === "visible" || y === "clip";
    if (xNonScrollable && !yNonScrollable)
        computed.overflowX = x === "visible" ? "auto" : "hidden";
    if (yNonScrollable && !xNonScrollable)
        computed.overflowY = y === "visible" ? "auto" : "hidden";
}
function resolveCurrentColor(computed) {
    const color = computed.color ?? "#000000ff";
    for (const property of [
        "borderTopColor",
        "borderRightColor",
        "borderBottomColor",
        "borderLeftColor",
    ]) {
        if (computed[property] === "currentColor")
            computed[property] = color;
    }
}
//# sourceMappingURL=resolver.js.map